import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EmployeeService } from '../../../../../services/employee.service';
import { Employee } from '../../../../../interfaces/Employee';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-add-participants-dialog',
  templateUrl: './add-participants-dialog.component.html',
  styleUrls: ['./add-participants-dialog.component.scss']
})
export class AddParticipantsDialogComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  searchControl = new FormControl('');
  allEmployees: { name: string; email: string }[] = [];
  filteredEmployees$!: Observable<{ name: string; email: string }[]>;
  selectedParticipants: { name: string; email: string }[] = [];

  constructor(
    private dialogRef: MatDialogRef<AddParticipantsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { currentParticipants: { name: string; email: string }[] },
    private employeeService: EmployeeService
  ) {
    // Initialize with current participants if provided
    if (data && data.currentParticipants) {
      this.selectedParticipants = [...data.currentParticipants];
    }
  }

  ngOnInit(): void {
    this.loadEmployees();
    this.setupFilter();
  }

  loadEmployees(): void {
    const namesToRemove = ['Shilpa Pahuja', 'Mukti Panchamia'];

    this.employeeService.getUsers().pipe(takeUntil(this._destroyed$)).subscribe(snapshotChanges => {
      const employeeData = snapshotChanges.map(w => {
        let emp = w.payload.toJSON() as Employee;
        if (w.key) emp['id'] = w.key;
        return emp;
      });

      // Filter active employees
      this.allEmployees = employeeData
        .filter(emp => emp.disabled !== true && !namesToRemove.includes(emp.userName))
        .map(emp => ({
          name: emp.userName,
          email: emp.email
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      this.setupFilter();
    });
  }

  setupFilter(): void {
    this.filteredEmployees$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map(searchValue => this.filterEmployees(searchValue || ''))
    );
  }

  filterEmployees(searchValue: string): { name: string; email: string }[] {
    const search = searchValue.toLowerCase().trim();
    if (!search) {
      return this.allEmployees;
    }
    return this.allEmployees.filter(emp =>
      emp.name.toLowerCase().includes(search) ||
      emp.email.toLowerCase().includes(search)
    );
  }

  isSelected(employee: { name: string; email: string }): boolean {
    return this.selectedParticipants.some(p => p.email === employee.email);
  }

  toggleSelection(employee: { name: string; email: string }): void {
    const index = this.selectedParticipants.findIndex(p => p.email === employee.email);

    if (index === -1) {
      // Add to selection
      this.selectedParticipants.push(employee);
    } else {
      // Remove from selection
      this.selectedParticipants.splice(index, 1);
    }
  }

  selectAll(): void {
    this.selectedParticipants = [...this.allEmployees];
  }

  clearAll(): void {
    this.selectedParticipants = [];
  }

  removeParticipant(participant: { name: string; email: string }): void {
    this.selectedParticipants = this.selectedParticipants.filter(p => p.email !== participant.email);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onAdd(): void {
    this.dialogRef.close(this.selectedParticipants);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
